// src/components/Layout/LeftToolbar/LeftToolbar.tsx
import React, { useCallback, useMemo, useState } from 'react';
import {
  MousePointer,
  Hand,
  Plus,
  ArrowRight,
  Square,
  Type,
  Undo2,
  Redo2,
  Trash2,
  Group,
  Save
} from 'lucide-react';
import { useUiStateStore } from '@stores/uiStateStore';
import { useScene } from 'src/hooks/useScene';
import { TEXTBOX_DEFAULTS } from 'src/config';
import { generateId } from 'src/utils';
import { GroupableElement } from 'src/features/groups/groupTypes';
import { useGroupStore } from '@/stores/groupStore';
import { useModelStore } from '@/stores/modelStore';
import { useSceneStore } from '@/stores/sceneStore';
import { createGroup as saveGroupToSupabase, updateGroup as updateGroupInSupabase } from '@/services/groupService';
import { iconPackManager } from '@/features/components/iconPacks/IconPackManager';
import { toast } from '@/stores/toastStore';

interface LeftToolbarProps {
  isLeftSidebarOpen: boolean;
  onOpenLeftSidebar: () => void;
}

export const LeftToolbar: React.FC<LeftToolbarProps> = ({
  isLeftSidebarOpen,
  onOpenLeftSidebar
}) => {
  const sceneActions = useScene();
  const { createTextBox } = sceneActions;
  const mode = useUiStateStore((state) => state.mode);
  const itemControls = useUiStateStore((state) => state.itemControls);
  const uiStateActions = useUiStateStore((state) => state.actions);
  const mousePosition = useUiStateStore((state) => state.mouse.position.tile);
  const groupActions = useGroupStore((state) => state.actions);
  const groups = useGroupStore((state) => state.groups);

  // Get diagram data for saving groups
  const items = useModelStore((state) => state.items);
  const views = useModelStore((state) => state.views);
  const colors = useModelStore((state) => state.colors);
  const historyVersion = useModelStore((state) => state.historyVersion);
  const modelActions = useModelStore((state) => state.actions);
  const sceneStoreActions = useSceneStore((state) => state.actions);
  const canUndo = historyVersion >= 0 && modelActions.canUndo();
  const canRedo = historyVersion >= 0 && modelActions.canRedo();

  // Save to Supabase state
  const [isSavingToSupabase, setIsSavingToSupabase] = useState(false);

  // Get selected elements (matching EditActions implementation)
  const selectedElements = useMemo(() => {
    const elements: GroupableElement[] = [];

    // Check for MULTI_SELECT
    if (itemControls?.type === 'MULTI_SELECT') {
      itemControls.items.forEach((item) => {
        if (
          item.type === 'ITEM' ||
          item.type === 'RECTANGLE' ||
          item.type === 'TEXTBOX' ||
          item.type === 'CONNECTOR'
        ) {
          elements.push({
            type: item.type as any,
            id: item.id
          });
        }
      });
    }
    // Check if multiple items are being dragged
    else if (mode.type === 'DRAG_ITEMS' && mode.items) {
      mode.items.forEach((item) => {
        if (
          item.type === 'ITEM' ||
          item.type === 'RECTANGLE' ||
          item.type === 'TEXTBOX' ||
          item.type === 'CONNECTOR'
        ) {
          elements.push({
            type: item.type as any,
            id: item.id
          });
        }
      });
    }
    // Check single selection
    else if (itemControls) {
      if (
        itemControls.type === 'ITEM' ||
        itemControls.type === 'RECTANGLE' ||
        itemControls.type === 'TEXTBOX' ||
        itemControls.type === 'CONNECTOR'
      ) {
        elements.push({
          type: itemControls.type as any,
          id: itemControls.id
        });
      }
    }

    return elements;
  }, [mode, itemControls]);

  // ✅ Check if selected elements belong to the same group
  const selectedGroup = useMemo(() => {
    if (selectedElements.length === 0) return undefined;

    // Get group for first element
    const firstGroup = groupActions.getGroupForElement(selectedElements[0]);
    if (!firstGroup) return undefined;

    // Check if ALL selected elements belong to the SAME group
    const allInSameGroup = selectedElements.every((el) => {
      const elGroup = groupActions.getGroupForElement(el);
      return elGroup?.id === firstGroup.id;
    });

    return allInSameGroup ? firstGroup : undefined;
  }, [selectedElements, groupActions, groups]); // ✨ Added groups dependency to update when name changes

  // Can group: 2+ elements selected and not already grouped
  const canGroup = useMemo(() => {
    if (selectedElements.length < 2) return false;

    // Check if any element is already in a group
    const hasGroupedElement = selectedElements.some((el) =>
      groupActions.isGrouped(el)
    );

    return !hasGroupedElement;
  }, [selectedElements, groupActions]);

  // ✅ Can show properties if a group is selected
  const canShowProperties = useMemo(() => {
    return selectedGroup !== undefined;
  }, [selectedGroup]);

  // Handler functions (matching EditActions implementation)
  const handleUndo = () => {
    modelActions.undo();
    if (sceneStoreActions.canUndo()) sceneStoreActions.undo();
    uiStateActions.setItemControls(null);
  };

  const handleRedo = () => {
    modelActions.redo();
    if (sceneStoreActions.canRedo()) sceneStoreActions.redo();
    uiStateActions.setItemControls(null);
  };

  const handleDelete = () => {
    selectedElements.forEach((element) => {
      if (element.type === 'ITEM') {
        sceneActions.deleteViewItem(element.id);
        sceneActions.deleteModelItem(element.id);
      } else if (element.type === 'CONNECTOR') {
        sceneActions.deleteConnector(element.id);
      } else if (element.type === 'RECTANGLE') {
        sceneActions.deleteRectangle(element.id);
      } else if (element.type === 'TEXTBOX') {
        sceneActions.deleteTextBox(element.id);
      }
    });
    uiStateActions.setItemControls(null);
  };

  const createTextBoxProxy = useCallback(() => {
    const textBoxId = generateId();

    createTextBox({
      ...TEXTBOX_DEFAULTS,
      id: textBoxId,
      tile: mousePosition
    });

    uiStateActions.setMode({
      type: 'TEXTBOX',
      showCursor: false,
      id: textBoxId
    });
  }, [uiStateActions, createTextBox, mousePosition]);

  const drawingTools = [
    {
      name: 'Select',
      icon: MousePointer,
      onClick: () => {
        uiStateActions.setMode({
          type: 'CURSOR',
          showCursor: true,
          mousedownItem: null
        });
      },
      isActive: mode.type === 'CURSOR' || mode.type === 'DRAG_ITEMS',
      shortcut: 'V'
    },
    {
      name: 'Pan',
      icon: Hand,
      onClick: () => {
        uiStateActions.setMode({
          type: 'PAN',
          showCursor: false
        });
        uiStateActions.setItemControls(null);
      },
      isActive: mode.type === 'PAN',
      shortcut: 'H'
    },
    {
      name: 'Add Icon',
      icon: Plus,
      onClick: () => {
        // Open the left sidebar to show icon library
        onOpenLeftSidebar();

        uiStateActions.setItemControls({
          type: 'ADD_ITEM'
        });
        uiStateActions.setMode({
          type: 'PLACE_ICON',
          showCursor: true,
          id: null
        });
      },
      isActive: mode.type === 'PLACE_ICON',
      shortcut: 'I'
    },
    {
      name: 'Rectangle',
      icon: Square,
      onClick: () => {
        uiStateActions.setMode({
          type: 'RECTANGLE.DRAW',
          showCursor: true,
          id: null
        });
      },
      isActive: mode.type === 'RECTANGLE.DRAW',
      shortcut: 'R'
    },
    {
      name: 'Connector',
      icon: ArrowRight,
      onClick: () => {
        uiStateActions.setMode({
          type: 'CONNECTOR',
          id: null,
          showCursor: true
        });
      },
      isActive: mode.type === 'CONNECTOR',
      shortcut: 'C'
    },
    {
      name: 'Text',
      icon: Type,
      onClick: createTextBoxProxy,
      isActive: mode.type === 'TEXTBOX',
      shortcut: 'T'
    }
  ];

  const editActions = [
    {
      name: 'Undo',
      icon: Undo2,
      onClick: handleUndo,
      isDisabled: !canUndo,
      shortcut: '⌘Z'
    },
    {
      name: 'Redo',
      icon: Redo2,
      onClick: handleRedo,
      isDisabled: !canRedo,
      shortcut: '⌘⇧Z'
    },
    {
      name: 'Delete',
      icon: Trash2,
      onClick: handleDelete,
      isDisabled: selectedElements.length === 0,
      shortcut: '⌫'
    }
  ];

  const handleGroupOrShowProperties = () => {
    if (selectedGroup) {
      // Group is selected - show properties
      console.log('📋 Opening group properties:', selectedGroup.name);
      //onEnsurePropertiesVisible?.();
    } else if (canGroup && selectedElements.length >= 2) {
      // No group selected but can create one
      const result = groupActions.createGroup(
        selectedElements,
        `Group ${Date.now().toString(36).slice(-4)}`
      );

      if (result.success) {
        toast.success('Selection grouped.');
        // Open properties panel to show the new group
        //   onEnsurePropertiesVisible?.();
      } else {
        console.error('❌ Failed to create group:', result.error);
        toast.error(result.error ?? 'Failed to group the selection.');
      }
    } else {
      console.warn('Cannot group: need at least 2 ungrouped elements');
    }
  };

  const handleUngroup = () => {
    if (!selectedGroup) {
      console.warn('Cannot ungroup: no group selected');
      return;
    }

    const result = groupActions.ungroup(selectedGroup.id);

    if (result.success) {
      toast.success(`Ungrouped "${selectedGroup.name}".`);
      // Clear selection after ungrouping
      uiStateActions.setItemControls(null);
    } else {
      console.error('❌ Failed to ungroup:', result.error);
      toast.error(result.error ?? 'Failed to ungroup the selection.');
    }
  };

  // ✨ NEW: Save group as reusable master group to Supabase
  const handleSaveAsReusableGroup = async () => {
    if (!selectedGroup) {
      console.warn('No group selected to save');
      return;
    }

    setIsSavingToSupabase(true);

    try {
      // Extract items that belong to this group
      const groupItemIds = selectedGroup.elements.map((el) => el.id);
      const groupModelItems = items.filter((item) => groupItemIds.includes(item.id));

      // Get view items (which have position/tile data) from the first view
      const currentView = views[0];
      const groupViewItems = currentView?.items?.filter((viewItem) =>
        groupItemIds.includes(viewItem.id)
      ) || [];

      // Merge ModelItem data with ViewItem position data
      const groupItems = groupModelItems.map((modelItem) => {
        const viewItem = groupViewItems.find((vi) => vi.id === modelItem.id);
        return {
          ...modelItem,
          tile: viewItem?.tile || { x: 0, y: 0 }, // Include position from view
          labelHeight: viewItem?.labelHeight,
          visible: viewItem?.visible,
          locked: viewItem?.locked,
          zIndex: viewItem?.zIndex
        };
      });

      // Extract icons that are referenced by the group items
      // Get icon IDs from items
      const referencedIconIds = groupItems
        .map((item) => (item as any).icon)
        .filter((iconId) => iconId !== undefined);

      // Fetch icon definitions from IconPackManager and convert to Icon format
      const groupIcons = referencedIconIds
        .map((iconId) => {
          const iconDef = iconPackManager.getIconById(iconId);
          if (!iconDef) {
            console.warn(`⚠️ Icon not found in IconPackManager: ${iconId}`);
            return null;
          }
          // Convert IconDefinition to Icon format (url instead of svgPath)
          return {
            id: iconDef.id,
            name: iconDef.name,
            url: iconDef.svgPath,
            collection: iconDef.pack,
            isIsometric: iconDef.isIsometric,
            source: iconDef.source
          };
        })
        .filter((icon) => icon !== null);

      // Extract colors (all colors might be relevant)
      const groupColors = colors;

      // Debug logging
      console.log('🔍 Saving group to Supabase:', {
        name: selectedGroup.name,
        itemCount: groupItems.length,
        iconCount: groupIcons.length,
        colorCount: groupColors.length,
        items: groupItems,
        icons: groupIcons,
        // Debug: Check if items have tile property
        itemsHaveTile: groupItems.map(item => ({
          id: item.id,
          hasTile: !!(item as any).tile,
          tile: (item as any).tile
        }))
      });
      console.log('🔍 First item FULL structure BEFORE saving:', JSON.stringify(groupItems[0], null, 2));

      // Validate data
      if (groupItems.length === 0) {
        throw new Error('No items found in group. Please ensure the group contains valid elements.');
      }

      // Check if this group is already linked to a master group (update) or new (create)
      const isUpdate = !!selectedGroup.masterGroupId;
      let data, error;

      if (isUpdate) {
        // Update existing master group
        console.log('📝 Updating existing master group:', selectedGroup.masterGroupId);
        const result = await updateGroupInSupabase(selectedGroup.masterGroupId!, {
          name: selectedGroup.name,
          description: `Updated from diagram on ${new Date().toLocaleDateString()}`,
          items: groupItems,
          icons: groupIcons,
          colors: groupColors,
        });
        data = result.data;
        error = result.error;
      } else {
        // Create new master group
        console.log('✨ Creating new master group');
        const result = await saveGroupToSupabase(
          selectedGroup.name,
          groupItems,
          {
            description: `Created from diagram on ${new Date().toLocaleDateString()}`,
            category: 'custom',
            icons: groupIcons,
            colors: groupColors,
            tags: [],
            isPublic: false,
          }
        );
        data = result.data;
        error = result.error;
      }

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from save operation');
      }

      // Link local group to master group
      if (!isUpdate) {
        groupActions.setMasterGroupId(selectedGroup.id, data.id);
      }

      // Success!
      const message = isUpdate
        ? `✅ Group "${selectedGroup.name}" updated in Supabase!\n\nAll changes have been saved.`
        : `✅ Group "${selectedGroup.name}" saved as reusable group!\n\nYou can now find it in the Groups Library.`;
      toast.success(message);
      console.log(`✅ Group ${isUpdate ? 'updated' : 'saved'} to Supabase:`, data);

      // Dispatch custom event to notify GroupsLibrary to refresh
      window.dispatchEvent(new CustomEvent('flowi-group-saved', { detail: { groupId: data.id } }));
    } catch (error) {
      console.error('❌ Failed to save group:', error);
      toast.error(`Failed to save group: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingToSupabase(false);
    }
  };

  return (
    <div
      data-editor-ui="true"
      className={`absolute flex flex-col gap-2 transition-all duration-300 ease-in-out`}
      style={{
        left: isLeftSidebarOpen ? 'calc(320px + 1rem)' : '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 20
      }}
    >
      {/* Drawing Tools Section */}
      <div className="flex flex-col gap-1 rounded-lg bg-white p-2 shadow-lg dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
        {drawingTools.map((tool) => {
          const IconComponent = tool.icon;
          return (
            <button
              key={tool.name}
              onClick={tool.onClick}
              title={`${tool.name} (${tool.shortcut})`}
              className={`flex items-center justify-center rounded-lg p-2.5 transition-colors ${
                tool.isActive
                  ? 'bg-blue-500 text-white dark:bg-blue-600'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-700'
              }`}
            >
              <IconComponent className="h-5 w-5" />
            </button>
          );
        })}
      </div>

      {/* Edit Actions Section */}
      <div className="flex flex-col gap-1 rounded-lg bg-white p-2 shadow-lg dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
        {editActions.map((action) => {
          const IconComponent = action.icon;
          return (
            <button
              key={action.name}
              onClick={action.onClick}
              disabled={action.isDisabled}
              title={`${action.name} (${action.shortcut})`}
              className={`flex items-center justify-center rounded-lg p-2.5 transition-colors ${
                action.isDisabled
                  ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-700'
              }`}
            >
              <IconComponent className="h-5 w-5" />
            </button>
          );
        })}
      </div>
      <div className="flex flex-col gap-1 rounded-lg bg-white p-2 shadow-lg dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
        {/* Group Button */}
        <button
          onClick={handleGroupOrShowProperties}
          disabled={!canGroup && !canShowProperties}
          title={
            selectedGroup
              ? `${selectedGroup.name} - Click to edit`
              : canGroup
                ? `Group Selected (Ctrl+G)${selectedElements.length < 2 ? ' - Select 2+ elements' : ''}`
                : 'Select 2+ elements to group'
          }
          className={`flex items-center justify-center rounded-lg p-2.5 transition-all ${
            selectedGroup
              ? 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400'
              : canGroup
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
                : 'text-gray-300 cursor-not-allowed dark:text-slate-600 opacity-50'
          }`}
        >
          <Group size={20} />
        </button>

        {/* Ungroup Button */}
        {selectedGroup && (
          <button
            onClick={handleUngroup}
            title={`Ungroup "${selectedGroup.name}" (Ctrl+Shift+G)`}
            className="flex items-center justify-center rounded-lg p-2.5 transition-colors text-gray-600 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-950 dark:hover:text-red-400"
          >
            <div className="relative">
              <Group size={20} />
              {/* X overlay to indicate ungroup */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-5 h-0.5 bg-red-500 rotate-45" />
              </div>
            </div>
          </button>
        )}

        {/* ✨ NEW: Save as Reusable Group Button */}
        {selectedGroup && (
          <button
            onClick={handleSaveAsReusableGroup}
            disabled={isSavingToSupabase}
            title={`Save "${selectedGroup.name}" as Reusable Group`}
            className={`flex items-center justify-center rounded-lg p-2.5 transition-colors ${
              isSavingToSupabase
                ? 'bg-gray-300 text-gray-500 cursor-wait dark:bg-gray-700'
                : 'bg-purple-500 hover:bg-purple-600 text-white'
            }`}
          >
            <Save size={20} />
          </button>
        )}
      </div>
    </div>
  );
};
